/**
 * Pipeline board for a listing (moved from ListingDetailPage.tsx): leads grouped by stage, with
 * Add Lead and View All Leads. Also exports the leads query and stage labels the Overview's
 * pipeline summary reads, so both stay on one query key.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { fetchListingLeads, type Lead, type LeadStage } from '../../../lib/leasing.api';
import { STAGE_COLORS } from '../lead-detail/shared-constants';
import { AddLeadModal } from '../modals/AddLeadModal.js';

/** The first page (100) of a listing's leads — shared by the board, the Overview summary and the showings lead picker. */
export function listingLeadsQueryOptions(listingId: string) {
  return {
    queryKey: ['listing', listingId, 'leads'] as const,
    queryFn: () => fetchListingLeads(listingId, { pageSize: 100 }),
  };
}

// ---------------------------------------------------------------------------
// Section: Pipeline Board
// ---------------------------------------------------------------------------

export const PIPELINE_STAGES: LeadStage[] = ['inquiring', 'showing', 'applying', 'verifying', 'deciding'];

export const STAGE_LABELS_PIPELINE: Record<LeadStage, string> = {
  inquiring: 'Inquiring',
  showing: 'Showing',
  applying: 'Applying',
  verifying: 'Verifying',
  deciding: 'Deciding',
  closed: 'Closed',
};

function pipelineFormatAge(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
}

interface LeadCardProps {
  lead: Lead;
  onClick: () => void;
}

function LeadCard({ lead, onClick }: LeadCardProps) {
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      className="rounded-md border border-border bg-card p-2.5 cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-colors text-sm"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Open lead for ${lead.prospectName ?? lead.prospectEmail}`}
    >
      <p className="font-medium text-foreground truncate">
        {lead.prospectName ?? lead.prospectEmail}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {pipelineFormatAge(lead.updatedAt)} ago
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline Board Component
// ---------------------------------------------------------------------------

interface PipelineBoardProps {
  listingId: string;
}

export function PipelineBoard({ listingId }: PipelineBoardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);

  const { data, isLoading, error } = useQuery(listingLeadsQueryOptions(listingId));

  const leads = data?.items ?? [];

  // Group leads by stage
  const byStage = new Map<LeadStage, Lead[]>();
  for (const stage of PIPELINE_STAGES) {
    byStage.set(stage, []);
  }
  for (const lead of leads) {
    const group = byStage.get(lead.stage as LeadStage);
    if (group) group.push(lead);
  }

  const handleLeadCreated = () => {
    void queryClient.invalidateQueries({ queryKey: ['listing', listingId, 'leads'] });
  };

  return (
    <section data-testid="pipeline-board-stub" className="mb-6">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-base font-semibold">Pipeline</h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              data-testid="add-lead-button"
              onClick={() => setShowAddLeadModal(true)}
              aria-label="Add a lead manually"
            >
              + Add Lead
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="pipeline-view-all-leads"
              onClick={() => navigate(`/leasing/leads?listingId=${listingId}`)}
            >
              View All Leads
            </Button>
          </div>
        </div>

        {isLoading && <Skeleton className="h-32 w-full" />}

        {error && (
          <div
            data-testid="pipeline-error"
            className="p-3 bg-destructive/10 border border-destructive/30 rounded text-destructive text-sm"
            role="alert"
          >
            {error instanceof Error ? error.message : 'Failed to load pipeline'}
          </div>
        )}

        {!isLoading && !error && (
          // AC-L3: full-width by default at >=768px — `md:grid md:grid-flow-col
          // md:auto-cols-fr` lets the 5 fixed PIPELINE_STAGES columns grow to fill the
          // reclaimed width equally, instead of staying pinned at a fixed w-44 (176px)
          // each with leftover whitespace. Below 768px there isn't room for 5 full-width
          // columns, so it falls back to the established horizontal-scroll + edge-fade
          // pattern (see LeasingLayout's subnav for the same treatment).
          <div className="relative">
            <div
              aria-hidden="true"
              className="md:hidden pointer-events-none absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-card to-transparent"
            />
            <div data-testid="pipeline-board-columns" className="flex gap-3 overflow-x-auto pb-2 md:grid md:grid-flow-col md:auto-cols-fr md:overflow-visible">
            {PIPELINE_STAGES.map((stage) => {
              const stageLeads = byStage.get(stage) ?? [];
              return (
                <div
                  key={stage}
                  data-testid={`pipeline-column-${stage}`}
                  className="flex-shrink-0 w-44 md:w-auto md:min-w-0 rounded-md border border-border bg-muted/30 p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {STAGE_LABELS_PIPELINE[stage]}
                    </p>
                    {stageLeads.length > 0 && (
                      <Badge
                        variant={STAGE_COLORS[stage]}
                        className="justify-center h-4 min-w-4 px-1 py-0 rounded-full text-[10px] font-bold"
                      >
                        {stageLeads.length}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    {stageLeads.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No leads</p>
                    ) : (
                      stageLeads.map((lead) => (
                        <LeadCard
                          key={lead.id}
                          lead={lead}
                          onClick={() => navigate(`/leasing/leads/${lead.id}`)}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>

      {showAddLeadModal && (
        <AddLeadModal
          listingId={listingId}
          onClose={() => setShowAddLeadModal(false)}
          onSuccess={handleLeadCreated}
        />
      )}
    </section>
  );
}
